/**
 * "Did the join actually land?" — asked of the CHAIN, not of whether a promise
 * rejected.
 *
 * THE INCIDENT THIS EXISTS FOR (2026-08-02, production)
 * A customer joined a shop. The CustomerShopAccount was minted on-chain. The
 * app said "ACCOUNT CREATION FAILED — KEEPING PREVIOUS SHOP" and discarded the
 * selection, leaving them staring at "FIND A SHOP TO START" while owning a
 * perfectly good shop account.
 *
 * `executeTransactionBlock` can throw on the RESPONSE after the node has
 * already accepted and executed the transaction — a dropped connection, a
 * failover mid-flight, a timeout. Every one of those looks identical to "the
 * write failed" at the call site, and it is not.
 *
 * So: a rejected promise is evidence that something went wrong, NOT evidence
 * that nothing happened. For an idempotent create, one cheap read settles it.
 *
 * THREE OUTCOMES, NOT TWO. "we looked and it is not there" and "we could not
 * look" are different facts, and collapsing them into a boolean is precisely
 * how the original bug told the customer something definite and wrong.
 */
import { fetchCustomerShopAccounts as defaultFetch } from './sui-service';
import { addressesEqual } from '../lib/shop-address';

export type ShopAccountConfirmation = 'exists' | 'absent' | 'unknown';

export interface ConfirmShopAccountDeps {
  fetchCustomerShopAccounts: (ownerAddress: string) => Promise<any[]>;
}

export async function confirmShopAccount(
  userAddress: string,
  shopOwnerAddress: string,
  deps: ConfirmShopAccountDeps = { fetchCustomerShopAccounts: defaultFetch },
): Promise<ShopAccountConfirmation> {
  let accounts: any[];
  try {
    accounts = (await deps.fetchCustomerShopAccounts(userAddress)) as any[];
  } catch {
    // The read failed. We know nothing new — say so rather than guessing.
    return 'unknown';
  }

  // A non-array means the shape changed under us, which is also "we don't know".
  // Returning 'absent' here would silently resurrect the original bug.
  if (!Array.isArray(accounts)) return 'unknown';

  const found = accounts.some((a: any) => {
    // The CSA's on-chain `shop` field is the shop OWNER'S ADDRESS, not the shop
    // object id, and Sui renders addresses with inconsistent padding/case —
    // `===` is the documented trap here. Malformed rows are skipped, not thrown on.
    const shop = a?.data?.content?.fields?.shop;
    return typeof shop === 'string' && addressesEqual(shop, shopOwnerAddress);
  });

  return found ? 'exists' : 'absent';
}
